import classNames from 'classnames'
import { X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'
import { ownedPortal } from '../../lib/navigation/ownedPortal'
import { Sheet } from './Sheet'
import { Text } from './Text'
import { IconButton } from './IconButton'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { getOverlayHost } from '../../lib/layout/overlayHost'

export type ModalSize = 'sm' | 'md' | 'lg' | 'fullscreen'

export interface ModalProps {
  open:      boolean
  onClose:   () => void
  title?:    string
  size?:     ModalSize
  children:  ReactNode
  /**
   * Prioridade de empilhamento. `base` (default) usa --z-overlay/--z-modal.
   * `above` usa --z-overlay-above/--z-modal-above pra renderizar acima de
   * outro modal/sheet já aberto. Repassada pro Sheet no mobile.
   */
  priority?: 'base' | 'above'
  /**
   * Exibe o botão "X" de fechar. Default `true`. Vale nas duas variantes
   * (sheet no mobile, overlay no desktop) e independe de `title` existir.
   */
  showClose?: boolean
}

const sizeClasses: Record<ModalSize, string> = {
  sm:         'max-w-sm',
  md:         'max-w-md',
  lg:         'max-w-lg',
  fullscreen: 'max-w-none w-full h-full rounded-none',
}

/**
 * Em viewport < 768px: renderiza como Sheet (bottom sheet).
 * Em viewport >= 768px: renderiza como overlay centralizado.
 * Fecha com Escape em ambos os casos.
 */
export function Modal({ open, onClose, title, size = 'md', children, priority = 'base', showClose = true }: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  useFocusTrap(open, containerRef)

  // Fecha com Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (typeof document === 'undefined') return null

  // Mobile: delega para Sheet (repassa priority pra empilhar corretamente
  // em cima de outro sheet quando priority="above").
  if (window.innerWidth < 768) {
    return (
      <Sheet open={open} onClose={onClose} title={title} priority={priority} showClose={showClose}>
        {children}
      </Sheet>
    )
  }

  if (!open) return null

  return ownedPortal(
    (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: priority === 'above' ? 'var(--z-overlay-above)' : 'var(--z-overlay)' }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 animate-backdrop-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={containerRef}
        className={classNames(
          'relative flex flex-col w-full',
          'bg-surface rounded-2xl shadow-xl overflow-hidden border border-surface-alt',
          'animate-fade-up',
          sizeClasses[size],
        )}
        style={{ zIndex: priority === 'above' ? 'var(--z-modal-above)' : 'var(--z-modal)' }}
      >
        {title ? (
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-surface-alt">
            <Text variant="heading-sm">{title}</Text>
            {showClose && (
              <IconButton icon={X} label="Fechar" variant="ghost" size="sm" onClick={onClose} className="shrink-0" />
            )}
          </div>
        ) : showClose && (
          // Sem título não há header — o X flutua sobre o conteúdo, com fundo
          // próprio pra não sumir sobre imagem/mídia.
          <IconButton
            icon={X}
            label="Fechar"
            size="sm"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 bg-surface/90 backdrop-blur-sm shadow-sm"
          />
        )}
        <div className="overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </div>
    ),
    // Frame do desktop quando ele existe, `document.body` fora dele. Mirar o
    // body direto abriria o diálogo com a largura da janela por cima de um app
    // de 430px.
    getOverlayHost(),
  )
}
